<template>
    <div class="explore-navigation">
        <div class="explore-navigation__inner">
            <a :href="pageOnLogin" :aria-label="$trans('link_to_landing_page')" class="nav-logo" />
            <a href="#main-content" class="skip-link">{{ 'skip_to_main_content'|trans }}</a>
            <nav class="explore-navigation__main-navigation" :aria-label="'main_navigation'|trans">
                <ul ref="menu" class="explore-navigation__main-list">
                    <li v-for="(item, key) in visibleItems" :key="key" :class="`explore-navigation__main-item explore-navigation__main-item--${item.key}`">
                        <router-link
                            v-if="['challenge', 'blog', 'focus', 'journal', 'discussion-board', 'webshop'].includes(item.key)"
                            :to="item.url"
                            class="explore-navigation__main-link"
                            tag="a"
                            :aria-label="item.label"
                            active-class="explore-navigation__main-link--active"
                        >
                            <span class="explore-navigation__main-label">
                                {{ item.label }}
                            </span>
                        </router-link>
                        <a v-else class="explore-navigation__main-link" :href="item.url" :aria-label="item.label">
                            <span class="explore-navigation__main-label">
                                {{ item.label }}
                            </span>
                        </a>
                    </li>

                    <li>
                        <b-dropdown
                            v-if="moreItems.length"
                            ref="more"
                            class="explore-navigation__more-dropdown-container"
                            variant="link"
                            size="lg"
                            no-caret
                            toggle-class="explore-navigation__more-dropdown-toggle"
                            menu-class="explore-navigation__more-dropdown"
                            right
                        >
                            <span class="explore-navigation__more-dropdown-background" />
                            <template slot="button-content">
                                {{ 'action.more'|trans() }}
                                <i class="explore-navigation__more-angle-down fas fa-angle-down" />
                            </template>

                            <b-dropdown-item v-for="moreItem in moreItems" :key="moreItem.key" :href="moreItem.url" :class="`explore-navigation__more-dropdown-link explore-navigation__more-dropdown-link--${moreItem.key}`">
                                {{ moreItem.label }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </li>
                </ul>
            </nav>

            <div ref="right" v-click-outside="closeUserNavigation" class="explore-navigation__right">
                <language-switch v-if="$can('switch-locale', user)" class="explore-navigation__language-switch" variant="compact" direction="right" />

                <div v-if="$can('access-pulse', user) && isObserver" class="explore-navigation__pulse">
                    <a v-b-tooltip.bottom class="explore-navigation__pulse-link" href="/pulse" title="Pulse" :aria-label="'link_to_analyze_label'|trans">
                        <i class="explore-navigation__pulse-icon fas fa-wave-sine" aria-hidden="true"/>
                    </a>
                </div>

                <div v-if="$can('access-admin', user)" class="explore-navigation__admin">
                    <a v-b-tooltip.bottom class="explore-navigation__admin-link" href="/admin3" title="Admin" :aria-label="'link_to_admin_label'|trans">
                        <i class="explore-navigation__admin-icon fas fa-key" aria-hidden="true"/>
                    </a>
                </div>

                <platform-navigation v-if="$can('navigate-platforms')" />

                <button
                    class="explore-navigation__user-navigation-toggle"
                    :class="{ 'explore-navigation__user-navigation-toggle--expanded': expandedUserNavigation }"
                    tabindex="0"
                    @click="toggleUserNavigation"
                    @keyup.down.enter="toggleUserNavigation"
                    aria-controls="user_navigation_menu"
                    :aria-expanded="expandedUserNavigation ? 'true' : 'false'"
                >
                    <figure class="explore-navigation__avatar-figure">
                        <img v-if="user" class="explore-navigation__avatar" :src="user.avatar.thumbnail_urls['150x150_cropped']" :alt="`${user.display_name}'s Avatar`">
                        <span v-if="user && (user.stats.notifications_count > 0 || user.stats.private_messages_count > 0)" class="explore-navigation__notifications-indicator" />
                    </figure>
                    <i class="explore-navigation__avatar-angle far fa-angle-down" :aria-label="'user_menu_toggle_indicator'|trans"/>
                </button>

                <transition name="slide-left-in">
                    <nav v-show="expandedUserNavigation" class="explore-navigation__user-navigation" id="user_navigation_menu">
                        <ul class="explore-navigation__user-navigation-list">
                            <template v-if="user.is_logged_in">
                                <li v-for="item in userItems" :key="item.key" :class="`explore-navigation__user-navigation-item explore-navigation__user-navigation-item--${item.key}`">
                                    <a class="explore-navigation__user-navigation-link" :href="item.url">
                                        <span class="explore-navigation__user-navigation-label">
                                            {{ item.label }}
                                        </span>

                                        <template v-if="item.key === 'notification-center' && user.stats && user.stats.notifications_count > 0">
                                            <span class="explore-navigation__user-navigation-indicator explore-navigation__user-navigation-indicator--notifications">
                                                {{ user.stats.notifications_count }}
                                            </span>
                                        </template>
                                        <template v-if="item.key === 'private-messages' && user.stats && user.stats.private_messages_count > 0">
                                            <span class="explore-navigation__user-navigation-indicator explore-navigation__user-navigation-indicator--private-messages">
                                                {{ user.stats.private_messages_count }}
                                            </span>
                                        </template>
                                    </a>
                                </li>
                            </template>
                            <template v-else>
                                <li class="explore-navigation__user-navigation-item explore-navigation__user-navigation-item--login">
                                    <a class="explore-navigation__user-navigation-link" :href="$path('login')">
                                        <span class="explore-navigation__user-navigation-label">
                                            {{ 'user.login'|trans() }}
                                        </span>
                                    </a>
                                </li>
                                <li class="explore-navigation__user-navigation-item explore-navigation__user-navigation-item--register">
                                    <a class="explore-navigation__user-navigation-link" :href="$path('register')">
                                        <span class="explore-navigation__user-navigation-label">
                                            {{ 'user.register'|trans() }}
                                        </span>
                                    </a>
                                </li>
                            </template>
                        </ul>
                    </nav>
                </transition>

                <sidebar-expander class="explore-navigation__sidebar-toggle" />
            </div>
        </div>
    </div>
</template>

<script>
    import PlatformNavigation from './PlatformNavigation.vue';
    import { getMetaDataByKey } from '../../../../Helpers';

    export default {
        name: 'ExploreNavigation',
        components: { PlatformNavigation },
        props: {
            items: {
                type: Array,
                required: true,
            },
            userItems: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                visibleItemsCount: 0,
                expandedUserNavigation: false,
                platformName: getMetaDataByKey('cmnty-platform-seo', 'title'),
                pageOnLogin: document.querySelector('meta[name="cmnty-page-on-login"]').content,
            };
        },
        computed: {
            visibleItems() {
                return this.items.slice(0, this.visibleItemsCount);
            },
            moreItems() {
                return this.items.slice(this.visibleItemsCount);
            },
            user() {
                return this.$store.getters['user/getUser'];
            },
            isObserver() {
                return this.user.role.type === 'observer';
            },
            screenWidth() {
                return this.$store.getters['configuration/getSetting']('screenWidth');
            },
        },
        watch: {
            screenWidth() {
                this.arrangeMenuItems();
            },
        },
        mounted() {
            this.arrangeMenuItems();
        },
        methods: {
            closeUserNavigation() {
                this.expandedUserNavigation = false;
            },
            toggleUserNavigation() {
                this.expandedUserNavigation = !this.expandedUserNavigation;
            },
            arrangeMenuItems() {
                if (!this.$refs.menu) {
                    return;
                }

                if (this.screenWidth < 900) {
                    this.visibleItemsCount = this.items.length;
                    return;
                }

                if (document.dir === 'rtl') {
                    this.arrangeMenuItemsRtl();
                    return;
                }

                const itemsNotVisibleCount = this.items.length - this.visibleItemsCount; // Count of how many items are not visible yet
                const marginLeft = 16; // Margin left of the 'user'menu that should remain empty
                const menuRightPoint = this.$refs.menu.offsetLeft + this.$refs.menu.offsetWidth; // Most right point of the menu
                const userMenuLeftPoint = this.$refs.right.offsetLeft; // Most left point of the 'user'menu

                if (menuRightPoint === 0) {
                    return;
                }

                let reservedSpace = 170; // Max width of a menu item
                if (itemsNotVisibleCount <= 1 && this.$refs.more) {
                    reservedSpace -= this.$refs.more.$el.offsetWidth; // Deduct width of the more item because it won't be visible after this
                }

                if (this.screenWidth >= 992) { // From 992px on the menu is centered and therefore gets an auto-margin on the left
                    reservedSpace /= 2; // Divide reserved space by 2 because it takes space both left and right
                }

                if (menuRightPoint + reservedSpace < userMenuLeftPoint - marginLeft && itemsNotVisibleCount >= 1) {
                    this.visibleItemsCount += 1;
                    this.$nextTick(this.arrangeMenuItems);
                } else if (menuRightPoint >= userMenuLeftPoint - marginLeft) {
                    this.visibleItemsCount -= 1;
                    this.$nextTick(this.arrangeMenuItems);
                }
            },
            arrangeMenuItemsRtl() {
                const itemsNotVisibleCount = this.items.length - this.visibleItemsCount; // Count of how many items are not visible yet
                const marginRight = 16; // Margin right of the 'user'menu that should remain empty
                const menuLeftPoint = this.$refs.menu.offsetLeft; // Most right point of the menu
                const userMenuRightPoint = this.$refs.right.offsetLeft + this.$refs.right.offsetWidth; // Most left point of the 'user'menu

                if (menuLeftPoint === 0) {
                    return;
                }

                let reservedSpace = 170; // Max width of a menu item
                if (itemsNotVisibleCount <= 1 && this.$refs.more) {
                    reservedSpace -= this.$refs.more.$el.offsetWidth; // Deduct width of the more item because it won't be visible after this
                }

                if (this.screenWidth >= 992) { // From 992px on the menu is centered and therefore gets an auto-margin on the left
                    reservedSpace /= 2; // Divide reserved space by 2 because it takes space both left and right
                }

                if (menuLeftPoint - reservedSpace > userMenuRightPoint + marginRight && itemsNotVisibleCount >= 1) {
                    this.visibleItemsCount += 1;
                    this.$nextTick(this.arrangeMenuItemsRtl);
                } else if (menuLeftPoint < userMenuRightPoint + marginRight) {
                    this.visibleItemsCount -= 1;
                    this.$nextTick(this.arrangeMenuItemsRtl);
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    .skip-link {
        position: absolute;
        top: -40px;
        left: 0;
        background: var(--color-primary);
        color: #fff;
        padding: 8px;
        z-index: 100;
        text-decoration: none;
    }

    .skip-link:focus {
        top: 0;
    }

    .explore-navigation {
        background-color: var(--color-menu-background);
        box-shadow: 0 0 10px 5px rgba(#000, .3);
        color: var(--color-menu-text);
        display: flex;
        height: 60px;
        left: 0;
        position: fixed;
        right: 0;
        top: 0;
        z-index: 3;

        @include max-breakpoint(screen-xs-max) {
            display: none;
        }

        &__inner {
            display: flex;
            height: 100%;
            position: relative;
            width: 100%;
            .nav-logo{
                background: no-repeat left center;
                background-image: var(--image-logo);
                background-size: cover;
                width: 200px;
                margin: 6px 10px;
                display: var(--logo-display-menu);
                position: absolute;
                height: -webkit-fill-available;
            }
            .nav-logo:focus-within {
                outline-offset: 4px;
            }
        }

        // Main navigation

        &__main-navigation {
            display: inline-flex;
            margin-left: $gutter-l;
            margin-right: auto;
            max-width: 100%;

            @media (min-width: 992px) {
                margin-left: auto;
            }
        }

        &__main-list {
            @include reset-list();
            display: flex;
            font-size: $font-m;
        }

        &__main-item {
            display: inline-flex;
            flex-shrink: 0;
            word-break: break-word;
            max-width: 170px;
            &:focus-within {
                outline: 2px solid var(--color-menu-text);
                border-bottom: none;
                outline-offset: -1px;
            }
        }

        &__main-link {
            align-items: center;
            color: var(--color-menu-text);
            display: inline-flex;
            font-size: $font-m;
            padding: $gutter-s $gutter-m;
            text-align: center;
            text-decoration: none;
            &:hover,
            &:focus,
            &--active,
            &[aria-expanded='true'] {
                color: var(--color-menu-text);
                border-bottom: 3px solid var(--color-menu-text);
                outline: none;
            }
        }

        // More dropdown

        &__more-dropdown-container {
            flex-shrink: 0;
        }

        /deep/ .explore-navigation {
            &__more-dropdown-toggle {
                align-items: center;
                background: transparent;
                border: 0;
                border-radius: 0;
                color: var(--color-menu-text);
                display: inline-flex;
                font-size: $font-m;
                height: 60px;
                justify-content: center;
                line-height: 1.2;
                padding: $gutter-s $gutter-m;
                text-decoration: none;
                transition: all .2s ease-in-out;

                &:hover,
                &:focus,
                &:active,
                &[aria-expanded="true"] {
                    color: var(--color-menu-text);
                    border-bottom: 2px solid var(--color-menu-text);
                    outline: none;
                }

                &:after {
                    display: none;
                }
            }

            &__more-dropdown {
                background-color: var(--color-menu-background);
                color: var(--color-menu-text);
                border: 0;
                border-radius: 0;
                margin: 0;
                padding: $gutter-s 0;
            }

            &__more-dropdown-link a {
                align-items: center;
                border-radius: 0;
                color: var(--color-menu-text);
                display: inline-flex;
                font-size: $font-s;

                &:hover,
                &:focus,
                &:active {
                    background: rgba(#fff, .075);
                    color: var(--color-menu-text);
                }
            }
        }

        &__more-angle-down {
            font-size: $font-s;
            margin-left: $gutter-s;
            margin-top: $gutter-xxs;
        }

        &__more-dropdown-background {
            background: rgba(#fff, .075);
            bottom: 0;
            display: inline-flex;
            height: 100%;
            pointer-events: none;
            position: absolute;
            top: 0;
            width: 100%;
        }

        // Right container

        &__right {
            display: flex;
            height: 100%;
            position: absolute;
            right: 0;
            top: 0;
        }

        // Admin button

        &__admin,
        &__pulse {
            flex-shrink: 0;
            position: relative;
        }

        &__admin-link,
        &__pulse-link {
            align-items: center;
            color: var(--color-menu-text);
            display: inline-flex;
            height: 100%;
            justify-content: center;
            transition: all .2s ease-in-out;
            width: 50px;

            &:hover,
            &:focus,
            &:active,
            &--active {
                background: rgba(#fff, .075);
                border-bottom: 2px solid var(--color-menu-text);
                color: var(--color-menu-text);
                outline: none;
                text-decoration: none;
            }
            &:focus-within {
                outline: 2px solid var(--color-menu-text);
                outline-offset: -1px;
            }
        }

        &__admin-icon,
        &__admin-icon {
            font-size: 20px;
        }

        // User dropdown

        &__user-navigation-toggle {
            align-items: center;
            color: var(--color-menu-text);
            cursor: pointer;
            display: inline-flex;
            height: 100%;
            justify-content: center;
            padding: 0 $gutter-s;
            transition: all .2s ease-in-out;
            width: 72px;
            background: none;
            border: none;
            &:hover,
            &:focus,
            &:active {
                background: rgba(#fff, .075);
                color: var(--color-menu-text);
                outline: none;
            }
            &:focus-within {
                outline: 2px solid var(--color-menu-text);
                outline-offset: -2px;
            }

            &--expanded {
                background: rgba(#fff, .075);
            }
        }

        &__avatar-figure {
            margin: 0;
            position: relative;
            transition: all .2s ease-in-out;
        }

        &__avatar {
            border-radius: var(--avatar-border-radius);
            height: 40px;
            width: 40px;
        }

        &__avatar-angle {
            margin-left: $gutter-xs;
        }

        &__notifications-indicator {
            background: #d9534f;
            border: 2px solid #fff;
            border-radius: 50%;
            bottom: -2px;
            box-shadow: 0 0 5px 2px rgba(0, 0, 0, .2);
            display: inline-flex;
            height: 14px;
            position: absolute;
            right: -2px;
            width: 14px;
        }

        &__user-navigation {
            background-color: var(--color-menu-background);
            position: absolute;
            right: 0;
            top: 100%;
        }

        &__user-navigation-list {
            @include reset-list();
            background: rgba(#fff, .075);
            min-width: 220px;
            padding: 8px 0;
        }

        &__user-navigation-link {
            align-items: center;
            border-radius: 0;
            color: var(--color-menu-text);
            display: inline-flex;
            font-size: $font-m;
            padding: $gutter-xs $gutter-m;
            text-decoration: none;
            width: 100%;

            &:hover,
            &:focus,
            &:active {
                background: rgba(#777, .075);
                color: var(--color-menu-text);
                cursor: pointer;
                text-decoration: none;
                outline-offset: -1px;
            }
        }

        &__user-navigation-indicator {
            align-items: center;
            background-color: var(--color-primary);
            border-radius: 50%;
            color: #fff;
            display: flex;
            font-size: $font-s;
            height: 20px;
            justify-content: center;
            margin-left: $gutter-m;
            width: 20px;

            &--notifications {
                background-color: #d9534f;
            }
        }

        &__sidebar-toggle {
            border-left: 1px solid rgba(#fff, .2);
            margin-left: $gutter-s;
            width: 54px;
        }

        &__language-switch {
            align-items: center;
            display: inline-flex;
            height: 100%;
            justify-content: center;
            transition: all .2s ease-in-out;
            width: 50px;

            &:hover,
            &:focus,
            &:active {
                color: var(--color-primary);
                outline: none;
                text-decoration: none;
            }

            &:focus-within {
                outline: 2px solid var(--color-menu-text);
                outline-offset: -2px;
            }
        }
    }
</style>
